import { apiFetch } from '../../auth/AuthContext';
import React, { useEffect, useState, useCallback } from 'react';
import './ValidationTab.css';

/* ── Types ──────────────────────────────────────────── */

interface CheckItem {
  category: string;
  check: string;
  status: 'PASS' | 'WARN' | 'FAIL';
  detail: string;
}

interface ReportTotals {
  total: number;
  pass: number;
  warn: number;
  fail: number;
}

interface ReportMeta {
  filename: string;
  timestamp: string;
  summary: string;
  totals: ReportTotals;
}

interface FullReport {
  timestamp: string;
  summary: string;
  totals: ReportTotals;
  failures: CheckItem[];
  warnings: CheckItem[];
  all_checks?: CheckItem[];
  by_category?: Record<string, CheckItem[]>;
}

/* ── Helpers ────────────────────────────────────────── */

function formatTs(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  if (h > 24) return `${Math.floor(h / 24)}d ago`;
  if (h > 0) return `${h}h ${m}m ago`;
  return `${m}m ago`;
}

function verdictFromSummary(summary: string): 'clear' | 'warn' | 'fail' {
  if (summary.startsWith('ALL CLEAR')) return 'clear';
  if (summary.startsWith('WARNINGS')) return 'warn';
  return 'fail';
}

const CATEGORY_ICONS: Record<string, string> = {
  Connectivity: 'plug',
  Schema: 'database',
  Freshness: 'clock',
  Integrity: 'shield',
  RowCounts: 'layers',
  Coverage: 'globe',
  Gaps: 'align-left',
  CrossDB: 'git-merge',
  Duplicates: 'copy',
  Storage: 'hard-drive',
  Pipeline: 'terminal',
};

const CATEGORY_ORDER = [
  'Connectivity',
  'Schema',
  'Freshness',
  'Integrity',
  'RowCounts',
  'Coverage',
  'Gaps',
  'CrossDB',
  'Duplicates',
  'Storage',
  'Pipeline',
];

/* ── Warning context — plain-language explanations per category ─────────── */

interface CategoryContext {
  impact: 'informational' | 'low' | 'medium';
  explanation: string;
  action: string;
}

const CATEGORY_CONTEXT: Record<string, CategoryContext> = {
  Freshness: {
    impact: 'low',
    explanation:
      "Some tables haven't received new rows within the expected freshness window. This usually means the daily ingestion cron ran but a handful of symbols or exchanges were slow to report — it does not mean data is lost.",
    action:
      'Check the Cron Health tab to confirm the daily_update job ran. If the job is healthy the rows will appear on the next cycle. Safe to ignore for historical research.',
  },
  Coverage: {
    impact: 'informational',
    explanation:
      "Certain coin/timeframe combinations are missing rows we'd expect to see. This happens when a new symbol was added to the config before its full history was backfilled, or when an exchange stopped listing a pair.",
    action:
      "Won't affect queries for other coins — just filter out or ignore the affected pairs. Backfill will close the gap over time.",
  },
  Duplicates: {
    impact: 'low',
    explanation:
      'The validator found rows with identical timestamps in a ReplacingMergeTree table. ClickHouse deduplicates these automatically in the background during merge operations, so duplicates are transient.',
    action:
      'Add FINAL to any query that needs strict deduplication (e.g. SELECT … FROM table FINAL). The duplicates self-resolve — no manual intervention needed.',
  },
  Storage: {
    impact: 'informational',
    explanation:
      'Table sizes have crossed a size threshold. This is purely informational — no data is at risk and queries are unaffected.',
    action:
      'Review disk capacity if approaching limits. Consider adjusting the TTL on high-frequency tables (e.g. depth scrapes) if storage becomes constrained.',
  },
  Gaps: {
    impact: 'low',
    explanation:
      'Gaps in the time-series were detected — stretches of candles or funding rows are missing. Often caused by exchange downtime, rate-limit back-off during ingestion, or the symbol going unlisted temporarily.',
    action:
      'Run a targeted backfill for the affected symbol+timeframe range. Gaps in rarely-used timeframes are typically low priority.',
  },
  Integrity: {
    impact: 'medium',
    explanation:
      'One or more data integrity checks failed — for example, open > high, or negative volume. Could indicate a bad API response that slipped through normalization.',
    action:
      'Inspect the flagged rows and patch or delete them. Use the Explorer tab to query the specific symbol and timestamp range.',
  },
  CrossDB: {
    impact: 'low',
    explanation:
      'A mismatch was found between databases — for example, a symbol present in Binance but missing in Bybit for the same period. Expected if exchanges list different coins.',
    action:
      "Informational unless you're running cross-exchange analysis that requires parity. No corrective action needed for most workflows.",
  },
  Connectivity: {
    impact: 'low',
    explanation:
      'A database connection check raised a warning. Could be a transient timeout during the validation run.',
    action:
      'Re-run the validator. If the warning persists, check that ClickHouse is healthy with the /data/health endpoint.',
  },
  Pipeline: {
    impact: 'medium',
    explanation:
      'The ingestion pipeline reported an anomaly — for example, an unexpected row count drop or a schema mismatch in a staging table.',
    action:
      'Check the daily_update log in the Cron Health tab for errors. May need a manual re-run of the affected ingestion script.',
  },
};

const IMPACT_COLOR: Record<string, string> = {
  informational: '#58a6ff',
  low: '#d29922',
  medium: '#f85149',
};

const IMPACT_LABEL: Record<string, string> = {
  informational: 'Informational',
  low: 'Low impact',
  medium: 'Action needed',
};

function WarningSummaryPanel({ warnings }: { warnings: CheckItem[] }) {
  const [open, setOpen] = useState(true);

  if (warnings.length === 0) return null;

  // Group by category, preserving order
  const grouped: Record<string, CheckItem[]> = {};
  for (const w of warnings) {
    (grouped[w.category] ??= []).push(w);
  }

  const categories = CATEGORY_ORDER.filter((c) => grouped[c]).concat(
    Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  );

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #1c1a0e 0%, #161b22 60%)',
        border: '2px solid #d29922',
        borderRadius: 12,
        marginBottom: 20,
        overflow: 'hidden',
        boxShadow: '0 0 24px #d2992222, 0 2px 8px #00000066',
      }}
    >
      {/* Panel header */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '16px 20px',
          background: 'linear-gradient(90deg, #d2992218 0%, transparent 100%)',
          border: 'none',
          borderBottom: open ? '1px solid #d2992244' : 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>⚠️</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              color: '#f0c040',
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: '0.01em',
            }}
          >
            Warning Briefing
          </div>
          <div style={{ color: '#d29922cc', fontSize: 11, marginTop: 2 }}>
            {warnings.length} warning{warnings.length !== 1 ? 's' : ''} across{' '}
            {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'} —
            click each item for details and recommended action
          </div>
        </div>
        <span
          style={{
            color: '#d29922',
            fontSize: 11,
            fontWeight: 600,
            background: '#d2992222',
            padding: '4px 10px',
            borderRadius: 20,
            border: '1px solid #d2992244',
            flexShrink: 0,
          }}
        >
          {open ? '▲ collapse' : '▼ expand'}
        </span>
      </button>

      {open && (
        <div>
          {categories.map((cat) => {
            const ctx = CATEGORY_CONTEXT[cat];
            const items = grouped[cat];
            const impact = ctx?.impact ?? 'low';
            const color = IMPACT_COLOR[impact];

            return (
              <div
                key={cat}
                style={{
                  borderBottom: '1px solid #d2992222',
                  padding: '16px 20px',
                }}
              >
                {/* Category title row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{ color: '#f0c040', fontWeight: 800, fontSize: 13 }}
                  >
                    {cat}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color,
                      background: color + '22',
                      padding: '3px 8px',
                      borderRadius: 4,
                      border: `1px solid ${color}88`,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {IMPACT_LABEL[impact]}
                  </span>
                  <span
                    style={{
                      color: '#d29922aa',
                      fontSize: 11,
                      marginLeft: 'auto',
                      background: '#d2992211',
                      padding: '2px 8px',
                      borderRadius: 10,
                      border: '1px solid #d2992233',
                    }}
                  >
                    {items.length} warning{items.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {/* Explanation */}
                {ctx && (
                  <p
                    style={{
                      color: '#c9d1d9',
                      fontSize: 12,
                      margin: '0 0 10px',
                      lineHeight: 1.7,
                    }}
                  >
                    {ctx.explanation}
                  </p>
                )}

                {/* Individual warnings */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    marginBottom: ctx ? 10 : 0,
                  }}
                >
                  {items.map((w, i) => (
                    <div
                      key={i}
                      style={{
                        background: '#0d1117cc',
                        border: '1px solid #d2992233',
                        borderLeft: '3px solid #d29922',
                        borderRadius: 6,
                        padding: '8px 12px',
                        display: 'flex',
                        gap: 10,
                        alignItems: 'flex-start',
                      }}
                    >
                      <span
                        style={{
                          color: '#f0c040',
                          fontSize: 12,
                          flexShrink: 0,
                          marginTop: 1,
                          fontWeight: 800,
                        }}
                      >
                        !
                      </span>
                      <div>
                        <div
                          style={{
                            color: '#e6edf3',
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {w.check}
                        </div>
                        {w.detail && (
                          <div
                            style={{
                              color: '#8b949e',
                              fontSize: 11,
                              marginTop: 3,
                              fontFamily: 'monospace',
                              lineHeight: 1.5,
                            }}
                          >
                            {w.detail}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action */}
                {ctx && (
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                      background: color + '10',
                      border: `1px solid ${color}55`,
                      borderLeft: `4px solid ${color}`,
                      borderRadius: 6,
                      padding: '10px 14px',
                    }}
                  >
                    <span
                      style={{
                        color: color,
                        fontSize: 13,
                        flexShrink: 0,
                        fontWeight: 800,
                        marginTop: 1,
                      }}
                    >
                      →
                    </span>
                    <p
                      style={{
                        color: '#e6edf3',
                        fontSize: 12,
                        margin: 0,
                        lineHeight: 1.7,
                        fontWeight: 500,
                      }}
                    >
                      {ctx.action}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Component ──────────────────────────────────────── */

export default function ValidationTab() {
  const [reports, setReports] = useState<ReportMeta[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fullReport, setFullReport] = useState<FullReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set());

  /* Fetch report list */
  const fetchReports = useCallback(async () => {
    try {
      const res = await apiFetch('/data/validation/reports');
      const data = await res.json();
      setReports(data.reports || []);
      if (data.reports?.length && !selectedFile) {
        setSelectedFile(data.reports[0].filename);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [selectedFile]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  /* Fetch full report when selection changes */
  useEffect(() => {
    if (!selectedFile) return;
    (async () => {
      try {
        const res = await apiFetch(`/data/validation/reports/${selectedFile}`);
        const data = await res.json();
        setFullReport(data);
        /* auto-expand categories with issues */
        const cats = new Set<string>();
        [...(data.failures || []), ...(data.warnings || [])].forEach(
          (c: CheckItem) => cats.add(c.category),
        );
        setExpandedCats(cats);
      } catch {
        setFullReport(null);
      }
    })();
  }, [selectedFile]);

  const toggleCat = (cat: string) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  /* Build category groups from warnings + failures (passes are counted) */
  const categoryGroups = React.useMemo(() => {
    if (!fullReport) return [];

    /* If we have all_checks, use them directly */
    if (fullReport.all_checks?.length) {
      const grouped: Record<string, CheckItem[]> = {};
      for (const c of fullReport.all_checks) {
        (grouped[c.category] ??= []).push(c);
      }
      return CATEGORY_ORDER.filter((cat) => grouped[cat]).map((cat) => ({
        category: cat,
        checks: grouped[cat],
      }));
    }

    /* If we have by_category, use it */
    if (fullReport.by_category) {
      return CATEGORY_ORDER.filter((cat) => fullReport.by_category![cat]).map(
        (cat) => ({ category: cat, checks: fullReport.by_category![cat] }),
      );
    }

    /* Fallback: reconstruct from warnings + failures only */
    const grouped: Record<string, CheckItem[]> = {};
    for (const c of [
      ...(fullReport.failures || []),
      ...(fullReport.warnings || []),
    ]) {
      (grouped[c.category] ??= []).push(c);
    }
    return CATEGORY_ORDER.filter((cat) => grouped[cat]).map((cat) => ({
      category: cat,
      checks: grouped[cat],
    }));
  }, [fullReport]);

  if (loading) {
    return (
      <div className="vt-container">
        <div className="vt-loading">Loading validation reports...</div>
      </div>
    );
  }

  if (!reports.length) {
    return (
      <div className="vt-container">
        <div className="vt-empty">
          <div className="vt-empty-icon">?</div>
          <div className="vt-empty-title">No Validation Reports</div>
          <div className="vt-empty-desc">
            Run <code>python scripts/validate_data.py --save</code> to generate
            your first report.
          </div>
        </div>
      </div>
    );
  }

  const selectedMeta = reports.find((r) => r.filename === selectedFile);
  const verdict = fullReport ? verdictFromSummary(fullReport.summary) : 'clear';

  return (
    <div className="vt-container">
      {/* ── Header ── */}
      <div className="vt-header">
        <div className="vt-header-left">
          <div className="vt-title">Data Validation Diary</div>
          <div className="vt-subtitle">
            Daily health checks across all 4 databases
          </div>
        </div>
        <div className="vt-header-right">
          <button className="vt-refresh-btn" onClick={fetchReports}>
            Refresh
          </button>
        </div>
      </div>

      {/* ── Timeline (diary entries) ── */}
      <div className="vt-layout">
        <div className="vt-sidebar">
          <div className="vt-sidebar-title">Report History</div>
          <div className="vt-timeline">
            {reports.map((r) => {
              const v = verdictFromSummary(r.summary);
              const isActive = r.filename === selectedFile;
              return (
                <button
                  key={r.filename}
                  className={`vt-entry ${isActive ? 'active' : ''}`}
                  onClick={() => setSelectedFile(r.filename)}
                >
                  <div className={`vt-entry-dot ${v}`} />
                  <div className="vt-entry-content">
                    <div className="vt-entry-date">{formatTs(r.timestamp)}</div>
                    <div className="vt-entry-time">
                      {formatTime(r.timestamp)}
                    </div>
                    <div className="vt-entry-stats">
                      <span className="vt-stat pass">
                        {r.totals.pass} passed
                      </span>
                      {r.totals.warn > 0 && (
                        <span className="vt-stat warn">
                          {r.totals.warn} warn
                        </span>
                      )}
                      {r.totals.fail > 0 && (
                        <span className="vt-stat fail">
                          {r.totals.fail} fail
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Report Detail ── */}
        <div className="vt-main">
          {fullReport && selectedMeta && (
            <>
              {/* Diary entry header */}
              <div className="vt-diary-header">
                <div className="vt-diary-date">
                  {formatTs(fullReport.timestamp)}
                </div>
                <div className="vt-diary-meta">
                  {formatTime(fullReport.timestamp)} UTC &middot;{' '}
                  {timeAgo(fullReport.timestamp)}
                </div>
              </div>

              {/* Verdict banner */}
              <div className={`vt-verdict ${verdict}`}>
                <div className="vt-verdict-icon">
                  {verdict === 'clear'
                    ? 'All Clear'
                    : verdict === 'warn'
                      ? 'Warnings'
                      : 'Failures'}
                </div>
                <div className="vt-verdict-summary">{fullReport.summary}</div>
              </div>

              {/* Warning briefing */}
              {fullReport.warnings?.length > 0 && (
                <WarningSummaryPanel warnings={fullReport.warnings} />
              )}

              {/* Summary cards */}
              <div className="vt-cards">
                <div className="vt-card">
                  <div className="vt-card-num" style={{ color: '#3fb950' }}>
                    {fullReport.totals.pass}
                  </div>
                  <div className="vt-card-label">Passed</div>
                </div>
                <div className="vt-card">
                  <div className="vt-card-num" style={{ color: '#d29922' }}>
                    {fullReport.totals.warn}
                  </div>
                  <div className="vt-card-label">Warnings</div>
                </div>
                <div className="vt-card">
                  <div className="vt-card-num" style={{ color: '#f85149' }}>
                    {fullReport.totals.fail}
                  </div>
                  <div className="vt-card-label">Failures</div>
                </div>
                <div className="vt-card">
                  <div className="vt-card-num" style={{ color: '#58a6ff' }}>
                    {fullReport.totals.total}
                  </div>
                  <div className="vt-card-label">Total Checks</div>
                </div>
              </div>

              {/* Category breakdown */}
              <div className="vt-categories">
                {categoryGroups.map(({ category, checks }) => {
                  const passes = checks.filter(
                    (c) => c.status === 'PASS',
                  ).length;
                  const warns = checks.filter(
                    (c) => c.status === 'WARN',
                  ).length;
                  const fails = checks.filter(
                    (c) => c.status === 'FAIL',
                  ).length;
                  const isOpen = expandedCats.has(category);
                  const catStatus =
                    fails > 0 ? 'fail' : warns > 0 ? 'warn' : 'pass';

                  return (
                    <div key={category} className="vt-cat">
                      <button
                        className="vt-cat-header"
                        onClick={() => toggleCat(category)}
                      >
                        <div className={`vt-cat-indicator ${catStatus}`} />
                        <div className="vt-cat-icon">
                          {CATEGORY_ICONS[category] || 'check'}
                        </div>
                        <div className="vt-cat-name">{category}</div>
                        <div className="vt-cat-stats">
                          {passes > 0 && (
                            <span className="vt-mini-stat pass">{passes}</span>
                          )}
                          {warns > 0 && (
                            <span className="vt-mini-stat warn">{warns}</span>
                          )}
                          {fails > 0 && (
                            <span className="vt-mini-stat fail">{fails}</span>
                          )}
                        </div>
                        <div className="vt-cat-arrow">
                          {isOpen ? '\u25BE' : '\u25B8'}
                        </div>
                      </button>

                      {isOpen && (
                        <div className="vt-cat-body">
                          {/* Show failures/warnings first, then passes */}
                          {checks
                            .sort((a, b) => {
                              const order = { FAIL: 0, WARN: 1, PASS: 2 };
                              return order[a.status] - order[b.status];
                            })
                            .map((c, i) => (
                              <div
                                key={i}
                                className={`vt-check ${c.status.toLowerCase()}`}
                              >
                                <div className="vt-check-icon">
                                  {c.status === 'PASS'
                                    ? '\u2713'
                                    : c.status === 'WARN'
                                      ? '!'
                                      : '\u2717'}
                                </div>
                                <div className="vt-check-content">
                                  <div className="vt-check-name">{c.check}</div>
                                  {c.detail && (
                                    <div className="vt-check-detail">
                                      {c.detail}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* If we only have warnings/failures (no all_checks), show a note about passed checks */}
                {!fullReport.all_checks &&
                  !fullReport.by_category &&
                  fullReport.totals.pass > 0 && (
                    <div className="vt-pass-note">
                      + {fullReport.totals.pass} additional checks passed across
                      all categories
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
